import React, { useEffect, useMemo, useRef, useState } from "react";
import { Table } from "@ad/anq-ui";
import classNames from "classnames";
import {
  DmpColumnType,
  Props,
  fixColumns,
  getAllColumns,
  renderSumRow,
} from "./common";

import styles from "./styles.module.scss";
import { plus2 } from "@/common/util/helpers";
import { TablePaginationConfig } from "@ad/anq-ui/lib/_components/_table";
import { SorterResult } from "@ad/anq-ui/lib/_components/_table/interface";
import get from "lodash/get";

/**
 * dmp特殊的table样式
 */
const DmpTable = (props: Props) => {
  const {
    tableTop,
    className,
    fixedHeader,
    tableTopStick,
    dataSource = [],
    sumRow,
    pagination,
    onChange,
    ...tableProps
  } = props;

  // 前端分页的时候的内部分页器
  // 用来分割超大数据量的数据, 来解决页面卡的不能动的问题
  const [innerPagination, setInnerPagination] = useState<TablePaginationConfig>(
    {
      current: 1,
      pageSize:
        typeof pagination === "object"
          ? pagination?.pageSize || pagination?.defaultPageSize || 10
          : Infinity,
    }
  );
  // 仅支持单列排序
  const [innerSorter, setInnerSorter] = useState(() => {
    const defaultSortColumn = (tableProps.columns?.find(
      (item) => item.defaultSortOrder
    ) || {}) as DmpColumnType;

    return {
      order: defaultSortColumn.defaultSortOrder,
      field: defaultSortColumn.dataIndex,
    };
  });

  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const sumRowInitialed = useRef(false);

  // 合计行的数据
  const sumRowData = useMemo(() => {
    if (!sumRow) {
      return null;
    }

    const allColumns = getAllColumns(props.columns);
    const result: any = { isSumRow: true };

    dataSource.forEach((data) => {
      allColumns.map((column) => {
        const columnKey = (column.key || column.dataIndex) as string;
        const isTextColumn = sumRow?.textColumn === columnKey;
        const isEmptyColumn =
          (typeof sumRow?.emptyColumns === "function" &&
            sumRow?.emptyColumns(column)) ||
          (Array.isArray(sumRow?.emptyColumns) &&
            sumRow?.emptyColumns.includes(columnKey as string));
        const isDataColumn =
          (typeof sumRow?.dataColumns === "function" &&
            sumRow?.dataColumns(column)) ||
          (Array.isArray(sumRow?.dataColumns) &&
            sumRow?.dataColumns.includes(columnKey)) ||
          !(isTextColumn || isEmptyColumn);

        if (isDataColumn) {
          result[columnKey] = plus2(
            result[columnKey] || 0,
            data[columnKey] || 0
          );
        }
      });
    });

    return sumRow.dataCalculator ? sumRow.dataCalculator(result) : undefined;
  }, [dataSource, props.columns, sumRow]);

  const sortedDataSource = useMemo(() => {
    const copyDataSource = [...dataSource];

    return copyDataSource.sort((a, b) => {
      if (innerSorter.order === "ascend") {
        return get(a, innerSorter.field) - get(b, innerSorter.field);
      } else if (innerSorter.order === "descend") {
        return get(b, innerSorter.field) - get(a, innerSorter.field);
      } else {
        return 0;
      }
    });
  }, [dataSource, innerSorter]);

  const pagedDataSource = useMemo(() => {
    // 后端分页
    if (typeof pagination === "object" && "current" in pagination) {
      return dataSource;
    }

    // 不分页
    if (!pagination) {
      return dataSource;
    }

    return sortedDataSource.slice(
      (innerPagination.current - 1) * innerPagination.pageSize,
      innerPagination.current * innerPagination.pageSize
    );
  }, [sortedDataSource, innerPagination.current, innerPagination.pageSize]);

  // 针对分组, 计算一下设计要求的columns之间的分隔符长度
  useEffect(() => {
    // 筛选出第一层右侧column是分组的列, 这些列的分隔符长度加长, 效果参见/pro/effects?tab=rct_analysis
    const columnIndexList = [];
    props.columns.forEach((item, index) => {
      if (props.columns[index + 1] && "children" in props.columns[index + 1]) {
        columnIndexList.push(index);
      }
    });

    // 暂时只支持分组一层
    const firstHeaderRowCells = document.querySelectorAll(
      `.${styles.table} thead tr:first-child th`
    );
    columnIndexList.forEach((columnIndex) => {
      const cellDom = firstHeaderRowCells[columnIndex];
      cellDom.classList.add(styles.tableHeaderSplitCell);
    });
  }, []);

  useEffect(() => {
    if (sumRow && sumRowInitialed.current) {
      sumRowInitialed.current = renderSumRow(wrapperRef.current);
    }
  }, [dataSource, sumRow]);

  const columnsWithSumRow = fixColumns(
    props.columns,
    props.sumRow,
    sumRowData,
    dataSource.length
  );
  const columns = fixColumns(props.columns);

  // TODO: w-full 用来修复anq-table的宽度计算导致的table宽度一直增加的问题, 后续或许可以去掉
  return (
    <>
      <div
        className="w-full"
        ref={(dom) => {
          wrapperRef.current = dom;
          if (sumRow && !sumRowInitialed.current) {
            sumRowInitialed.current = renderSumRow(dom);
          }
        }}
      >
        {tableTop ? (
          <div
            className={classNames(styles.tableTop, {
              ["sticky z-30"]: tableTopStick,
            })}
            style={{ top: tableTopStick ? tableTopStick : undefined }}
          >
            {tableTop}
          </div>
        ) : null}
        <Table
          className={classNames(
            tableTop ? styles.borderedTable : styles.borderedTableWithoutHeader,
            styles.table,
            className,
            {
              [styles.withoutPagination]: !pagination,
              [styles.fixedHeader]: fixedHeader,
            }
          )}
          pagination={
            typeof pagination === "object"
              ? {
                  showSizeChanger: true,
                  total: dataSource.length,
                  ...pagination,
                }
              : pagination
          }
          onChange={(...args) => {
            if (args[0].pageSize) {
              setInnerPagination({
                pageSize: args[0].pageSize,
                current: args[0].current,
              });
            }

            const sortResult = args[2] as SorterResult<any>;
            setInnerSorter({
              order: sortResult.order,
              field: sortResult.field,
            });
            onChange?.(...args);
          }}
          bordered={false}
          {...tableProps}
          dataSource={pagedDataSource}
          columns={columns}
        />
        {sumRow && (
          <div className="h-0 w-0 overflow-hidden">
            <Table
              {...tableProps}
              className="DmpTable__copy"
              pagination={false}
              dataSource={sumRowData ? [sumRowData] : []}
              columns={columnsWithSumRow}
            />
          </div>
        )}
      </div>
      <style>
        {`
          .anq-table-header.anq-table-sticky-holder {
            ${
              fixedHeader
                ? `top: ${fixedHeader === true ? 0 : fixedHeader}px;!important`
                : ""
            }
          }

          .anq-table-sticky-anchor {
            height: 0 !important;
          }
        `}
      </style>
    </>
  );
};

export default DmpTable;
